import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Navigation } from '@/components/Navigation';
import { Check, Zap, Crown, ArrowLeft, ExternalLink } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const Pricing = () => {
  const { user } = useAuth();
  const [isYearly, setIsYearly] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      description: 'Get started with basic AI humanization',
      monthlyPrice: 0,
      yearlyPrice: 0,
      credits: 3,
      whopLinks: {
        monthly: null,
        yearly: null
      },
      features: [
        '3 Humanizations / Visuals',
        'Basic Features',
        'Standard Processing',
        'Community Support'
      ],
      popular: false,
      icon: Zap,
      isFree: true
    },
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for regular content creators',
      monthlyPrice: 9,
      yearlyPrice: 8.25,
      credits: 100,
      whopLinks: {
        monthly: 'https://whop.com/checkout/plan_qWad2oY6Z7LJo?d2c=true',
        yearly: 'https://whop.com/checkout/plan_LGAE6Vvcrc6i2?d2c=true'
      },
      features: [
        '100 Humanizations',
        'Copy & Download',
        'Multiple Visuals',
        'Email Support',
        'Mobile Preview'
      ],
      popular: true,
      icon: Zap
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      description: 'For power users who need unlimited access',
      monthlyPrice: 20,
      yearlyPrice: 16.67,
      credits: null, // Unlimited
      whopLinks: {
        monthly: 'https://whop.com/checkout/plan_hE4PRuIAwKcn8?d2c=true',
        yearly: 'https://whop.com/checkout/plan_SoKmnSPDDOdqJ?d2c=true'
      },
      features: [
        'Unlimited Humanizations',
        'Copy & Download',
        'Multiple Visuals',
        'Priority Processing',
        'Priority Support',
        'Advanced Analytics'
      ],
      popular: false,
      icon: Crown
    },
    {
      id: 'lifetime',
      name: 'Lifetime',
      description: 'Pay once, use forever',
      monthlyPrice: null,
      yearlyPrice: null,
      lifetimePrice: 299,
      credits: null, // Unlimited
      whopLinks: {
        monthly: null,
        yearly: null,
        lifetime: 'https://whop.com/checkout/plan_QuZfuFJ0uXwSO?d2c=true'
      },
      features: [
        'Unlimited Humanizations',
        'Unlimited Visuals',
        'Copy & Download',
        'Priority Processing',
        'Priority Support',
        'Advanced Analytics',
        'Lifetime Updates',
        'No Recurring Fees'
      ],
      popular: false,
      icon: Crown,
      isLifetime: true
    }
  ];

  useEffect(() => {
    if (user) {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchCurrentSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          plans (*)
        `)
        .eq('user_id', user?.id)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        return;
      }

      setCurrentSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const getCurrentPlanId = () => {
    if (!currentSubscription?.plans) return null;
    
    const planName = currentSubscription.plans.name.toLowerCase();
    if (planName.includes('free')) return 'free';
    if (planName.includes('basic')) return 'basic';
    if (planName.includes('unlimited')) return 'unlimited';
    if (planName.includes('lifetime')) return 'lifetime';
    return null;
  };

  const isCurrentPlan = (planId: string) => {
    return getCurrentPlanId() === planId;
  };

  const handleCheckout = (whopUrl: string | null) => {
    if (!whopUrl) return;
    // Open Whop checkout in a new tab
    window.open(whopUrl, '_blank');
  };

  const getCheckoutUrl = (plan: any) => {
    let baseUrl;
    
    if (plan.isLifetime) {
      baseUrl = plan.whopLinks.lifetime;
    } else {
      baseUrl = isYearly ? plan.whopLinks.yearly : plan.whopLinks.monthly;
    }
    
    // If user is logged in, pass their email to Whop for better account linking
    if (user?.email && baseUrl) {
      return `${baseUrl}&email=${encodeURIComponent(user.email)}`;
    }
    
    return baseUrl;
  };

  const getPrice = (plan: any) => {
    if (plan.isLifetime) return plan.lifetimePrice;
    return isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getSavings = (plan: any) => {
    if (plan.isFree) return { amount: 0, percentage: 0 };
    const monthlyCost = plan.monthlyPrice * 12;
    const yearlyCost = plan.yearlyPrice * 12; // Since yearly price is already per month in the image
    const savings = monthlyCost - yearlyCost;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { amount: savings, percentage };
  };

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <div className="hidden sm:block h-6 w-px bg-border" />
              <h1 className="text-xl sm:text-2xl font-bold text-primary">Pricing Plans</h1>
            </div>
            <div className="w-full sm:w-auto">
              <Navigation />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            Choose Your Perfect Plan
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Transform your AI-generated content into natural, human-like text with our powerful humanization tools.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <Label htmlFor="billing-toggle" className={!isYearly ? 'text-primary font-medium' : 'text-muted-foreground'}>
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={isYearly}
              onCheckedChange={setIsYearly}
            />
            <Label htmlFor="billing-toggle" className={isYearly ? 'text-primary font-medium' : 'text-muted-foreground'}>
              Yearly
            </Label>
            <Badge variant="secondary" className="ml-2">
              Save up to 17%
            </Badge>
          </div>
          {/* Current Subscription Status */}
          {currentSubscription && (
            <div className="mb-8 p-4 bg-card border border-border rounded-lg">
              <div className="text-center">
                <h3 className="font-semibold mb-2">Current Plan</h3>
                <div className="flex items-center justify-center gap-4">
                  <Badge variant="default">
                    {currentSubscription.plans?.name} - {currentSubscription.billing_cycle}
                  </Badge>
                  <div className="text-sm text-muted-foreground">
                    Credits: {currentSubscription.credits_remaining === null ? 'Unlimited' : currentSubscription.credits_remaining}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            const savings = getSavings(plan);
            
            return (
              <Card key={plan.id} className={`relative ${
                plan.popular ? 'ring-2 ring-primary shadow-glow' : ''
              } ${
                plan.isLifetime ? 'ring-2 ring-warning shadow-glow' : ''
              } ${
                isCurrentPlan(plan.id) ? 'ring-2 ring-success bg-success/5' : ''
              }`}>
                {plan.popular && !isCurrentPlan(plan.id) && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    Most Popular
                  </Badge>
                )}
                {plan.isLifetime && !isCurrentPlan(plan.id) && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-warning text-warning-foreground">
                    Best Value
                  </Badge>
                )}
                {isCurrentPlan(plan.id) && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-success">
                    Current Plan
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <p className="text-muted-foreground">{plan.description}</p>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Pricing */}
                   <div className="text-center">
                     <div className="text-4xl font-bold mb-2">
                       {plan.isFree ? (
                         <span>Free</span>
                       ) : plan.isLifetime ? (
                         <>
                           ${Math.round(getPrice(plan))}
                           <span className="text-lg font-normal text-muted-foreground">
                             {' '}once
                           </span>
                         </>
                       ) : (
                         <>
                           ${Math.round(getPrice(plan))}
                           <span className="text-lg font-normal text-muted-foreground">
                             /{isYearly ? 'mo' : 'mo'}
                           </span>
                           {isYearly && !plan.isFree && (
                             <span className="text-lg font-normal text-muted-foreground">
                               • ${Math.round(plan.yearlyPrice * 12)}/yr
                             </span>
                           )}
                         </>
                       )}
                     </div>
                     {plan.isLifetime ? (
                       <div className="text-sm text-success font-medium">
                         Save up to 90% on recurring fees
                       </div>
                     ) : isYearly && !plan.isFree ? (
                       <div className="text-sm text-success">
                         Save ${((plan.monthlyPrice - plan.yearlyPrice) * 12).toFixed(2)}/year
                       </div>
                     ) : null}
                     <div className="text-sm text-muted-foreground mt-1">
                       {plan.credits === null ? 'Unlimited credits' : 
                        plan.credits === 1 ? '1 credit' : 
                        `${plan.credits.toLocaleString()} credits`}
                     </div>
                   </div>

                  {/* Features */}
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  {isCurrentPlan(plan.id) ? (
                    <Button
                      className="w-full"
                      size="lg"
                      variant="outline"
                      disabled
                    >
                      Current Plan
                    </Button>
                  ) : plan.isFree ? (
                    <Button
                      className="w-full"
                      size="lg"
                      variant="outline"
                      disabled={!!currentSubscription}
                    >
                      {currentSubscription ? 'Downgrade Available' : 'Free Plan'}
                    </Button>
                  ) : (
                     <Button
                       className="w-full"
                       size="lg"
                       variant={plan.popular || plan.isLifetime ? 'default' : 'outline'}
                       onClick={() => handleCheckout(getCheckoutUrl(plan))}
                     >
                       <span className="flex items-center gap-2">
                         {plan.isLifetime ? 'Get Lifetime Access' : 
                          currentSubscription ? 'Upgrade to' : 'Get Started with'} {!plan.isLifetime && plan.name}
                         <ExternalLink className="w-4 h-4" />
                       </span>
                     </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">No Hidden Fees</h3>
              <p className="text-sm text-muted-foreground">
                All plans include everything listed with no surprise charges.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Instant Access</h3>
              <p className="text-sm text-muted-foreground">
                Start humanizing your content immediately after purchase.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Premium Quality</h3>
              <p className="text-sm text-muted-foreground">
                Industry-leading AI humanization technology.
              </p>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground">
            Questions? <Link to="/auth" className="text-primary hover:underline">Contact our support team</Link> for help choosing the right plan.
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;